import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import styled from 'styled-components'
import { Badge, Form, Button } from 'react-bootstrap'
import { Package, Trash2 } from 'lucide-react'
import { GroupPlacement, SpawnArea, ObjectGroup } from '../types'

// Fix Leaflet default icon path issue
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

const ViewerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 8px;
  overflow: hidden;
  background: #1a1a1a;
  border: 1px solid #333;
  display: flex;
`

const MapContainer = styled.div<{ $isDragOver?: boolean; $hasSelectedArea?: boolean }>`
  flex: 1;
  position: relative;
  height: 100%;
  transition: all 0.2s ease;
  overflow: hidden; /* Prevent content from breaking out */

  .leaflet-container {
    height: 100%;
    width: 100%;
    border-radius: 8px 0 0 8px;
    position: relative;
    z-index: 1;
  }

  ${(props) =>
    props.$hasSelectedArea &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 3px dashed ${props.$isDragOver ? '#007bff' : 'transparent'};
      border-radius: 8px 0 0 8px;
      pointer-events: none;
      z-index: 10; /* Lower z-index to stay within container */
      background: ${props.$isDragOver ? 'rgba(0,123,255,0.1)' : 'transparent'};
      box-sizing: border-box; /* Include border in dimensions */
    }
  `}
`

const SpawnAreaSidebar = styled.div`
  width: 280px;
  background: linear-gradient(180deg, #1e1e1e 0%, #1a1a1a 100%);
  border-left: 1px solid #333;
  overflow-y: auto;
  color: white;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
`

const SidebarHeader = styled.div`
  padding: 1.5rem 1.25rem 1rem;
  background: linear-gradient(135deg, #2a2a2a 0%, #252525 100%);
  border-bottom: 1px solid #333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`

const SidebarTitle = styled.h5`
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SidebarSubtitle = styled.div`
  font-size: 0.7rem;
  color: #60a5fa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
`

const SpawnAreasList = styled.div`
  padding: 1.25rem;
  flex: 1;
  overflow-y: auto;
`

const SpawnAreaItem = styled.div<{
  $type: 'water' | 'land' | 'air'
  $isHovered?: boolean
  $isSelected?: boolean
}>`
  padding: 1rem;
  margin-bottom: 0.75rem;
  border-radius: 12px;
  cursor: pointer;
  border: 2px solid
    ${(props) => {
      if (props.$isSelected) {
        switch (props.$type) {
          case 'water':
            return '#60a5fa'
          case 'land':
            return '#4ade80'
          case 'air':
            return '#fbbf24'
          default:
            return '#007bff'
        }
      }
      return 'transparent'
    }};
  background: ${(props) => {
    const baseColor =
      props.$type === 'water'
        ? '96,165,250'
        : props.$type === 'land'
        ? '74,222,128'
        : props.$type === 'air'
        ? '251,191,36'
        : '255,255,255'

    if (props.$isSelected) return `rgba(${baseColor}, 0.15)`
    if (props.$isHovered) return `rgba(${baseColor}, 0.08)`
    return 'rgba(255, 255, 255, 0.04)'
  }};
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    background: ${(props) => {
      const baseColor =
        props.$type === 'water'
          ? '96,165,250'
          : props.$type === 'land'
          ? '74,222,128'
          : props.$type === 'air'
          ? '251,191,36'
          : '255,255,255'
      return `rgba(${baseColor}, 0.12)`
    }};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) => {
      switch (props.$type) {
        case 'water':
          return 'linear-gradient(90deg, #60a5fa, #3b82f6)'
        case 'land':
          return 'linear-gradient(90deg, #4ade80, #22c55e)'
        case 'air':
          return 'linear-gradient(90deg, #fbbf24, #f59e0b)'
        default:
          return '#666'
      }
    }};
    border-radius: 12px 12px 0 0;
    opacity: ${(props) => (props.$isSelected ? 1 : 0.7)};
  }
`

const SpawnAreaName = styled.h6`
  font-weight: 700;
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;
  color: white;
  line-height: 1.2;
`

const SpawnAreaType = styled.div<{ $type: 'water' | 'land' | 'air' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: ${(props) => {
    switch (props.$type) {
      case 'water':
        return '#60a5fa'
      case 'land':
        return '#4ade80'
      case 'air':
        return '#fbbf24'
      default:
        return '#999'
    }
  }};
  background: ${(props) => {
    switch (props.$type) {
      case 'water':
        return 'rgba(96,165,250,0.1)'
      case 'land':
        return 'rgba(74,222,128,0.1)'
      case 'air':
        return 'rgba(251,191,36,0.1)'
      default:
        return 'rgba(153,153,153,0.1)'
    }
  }};
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid
    ${(props) => {
      switch (props.$type) {
        case 'water':
          return 'rgba(96,165,250,0.3)'
        case 'land':
          return 'rgba(74,222,128,0.3)'
        case 'air':
          return 'rgba(251,191,36,0.3)'
        default:
          return 'rgba(153,153,153,0.3)'
      }
    }};
`

const GroupSummary = styled.div`
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border-left: 3px solid #60a5fa;
`

interface MapViewerProps {
  spawnAreas: SpawnArea[]
  groupPlacements: GroupPlacement[]
  onSpawnAreaDrop: (spawnAreaId: string) => void
  onSpawnAreaClick?: (spawnArea: SpawnArea) => void
  draggedGroup: string | null
  selectedSpawnArea?: SpawnArea | null
  isDragOver?: boolean
  onDragOver?: () => void
  onDragLeave?: () => void
  onDrop?: (e: React.DragEvent) => void
  availableGroups: ObjectGroup[]
  getSpawnSettings: (groupId: string, spawnAreaId: string) => { objectCount: number }
  updateSpawnSettings: (
    groupId: string,
    spawnAreaId: string,
    settings: { objectCount: number }
  ) => void
  removeGroupPlacement: (groupId: string, spawnAreaId: string) => void
}

const MapViewer: React.FC<MapViewerProps> = ({
  spawnAreas,
  groupPlacements,
  onSpawnAreaDrop,
  onSpawnAreaClick,
  draggedGroup,
  selectedSpawnArea,
  isDragOver = false,
  onDragOver,
  onDragLeave,
  onDrop,
  availableGroups,
  getSpawnSettings,
  updateSpawnSettings,
  removeGroupPlacement,
}) => {
  const mapRef = useRef<L.Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const spawnLayersRef = useRef<L.LayerGroup>(new L.LayerGroup())
  const polygonStyleCache = useRef<Map<string, any>>(new Map())
  const polygonRefs = useRef<Map<string, L.Polygon>>(new Map())

  const [hoveredArea, setHoveredArea] = useState<string | null>(null)
  const [highlightedArea, setHighlightedArea] = useState<string | null>(null)

  const [mapInitialized, setMapInitialized] = useState<boolean>(false)
  const [boundsSet, setBoundsSet] = useState<boolean>(false)

  // Sync selected area with parent component
  const selectedArea = selectedSpawnArea?.id || null

  // Debug logging to track prop changes and polygon storage
  useEffect(() => {
    console.log(
      '📥 MapViewer received selectedSpawnArea:',
      selectedSpawnArea?.name || 'null',
      'ID:',
      selectedArea
    )
    console.log('🎯 MapViewer drag area should show:', !!selectedArea, 'isDragOver:', isDragOver)
  }, [selectedSpawnArea, selectedArea, isDragOver])

  // Reset bounds flag when new spawn areas are loaded
  useEffect(() => {
    if (spawnAreas.length > 0) {
      setBoundsSet(false) // Allow auto-fit for new spawn areas
    }
  }, [spawnAreas.length])

  // Debounced hover handlers to reduce flickering
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null)
  const highlightTimeout = useRef<NodeJS.Timeout | null>(null)
  const styleUpdateTimeout = useRef<NodeJS.Timeout | null>(null)
  const lastStyleUpdate = useRef<string>('')

  const setHoveredAreaDebounced = useCallback((areaId: string | null) => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current)
    }
    hoverTimeout.current = setTimeout(() => {
      setHoveredArea(areaId)
    }, 150) // Increased debounce time
  }, [])

  const setHighlightedAreaDebounced = useCallback((areaId: string | null) => {
    if (highlightTimeout.current) {
      clearTimeout(highlightTimeout.current)
    }
    highlightTimeout.current = setTimeout(() => {
      setHighlightedArea(areaId)
    }, 100) // Debounce highlight changes
  }, [])

  const getAreaTypeColor = useCallback((type: 'water' | 'land' | 'air'): string => {
    switch (type) {
      case 'water':
        return '#60a5fa'
      case 'land':
        return '#4ade80'
      case 'air':
        return '#fbbf24'
      default:
        console.log(`⚠️ Unknown type: "${type}", defaulting to gray`)
        return '#666'
    }
  }, [])

  // Memoize area type lookup
  const areaTypeMap = useMemo(() => {
    const map = new Map<string, 'water' | 'land' | 'air'>()
    spawnAreas.forEach((area) => {
      map.set(area.id, area.type)
    })
    return map
  }, [spawnAreas])

  // Calculate polygon style efficiently - removed dependencies that cause frequent updates
  const getPolygonStyle = useCallback(
    (
      areaId: string,
      isSelected: boolean,
      isHighlighted: boolean,
      isHovered: boolean,
      areaType: 'water' | 'land' | 'air'
    ) => {
      const color = getAreaTypeColor(areaType)

      // Special styling for selected area during drag operations
      if (isSelected && draggedGroup && isDragOver) {
        return {
          fillOpacity: 0.6,
          opacity: 1,
          weight: 6,
          color: '#007bff',
          fillColor: '#007bff',
          dashArray: '10,5', // Dashed border to indicate drop target
        }
      }

      if (selectedArea && !isSelected) {
        return { fillOpacity: 0, opacity: 0, weight: 2, color, fillColor: color }
      } else if (isSelected) {
        return { fillOpacity: 0.8, opacity: 1, weight: 4, color, fillColor: color }
      } else if (isHighlighted && !selectedArea) {
        return { fillOpacity: 0.6, opacity: 1, weight: 3, color, fillColor: color }
      } else if (highlightedArea && !isHighlighted && !selectedArea) {
        return { fillOpacity: 0.1, opacity: 0.3, weight: 1, color, fillColor: color }
      } else if (!selectedArea) {
        return { fillOpacity: 0.4, opacity: 0.8, weight: 2, color, fillColor: color }
      }
      return { fillOpacity: 0.4, opacity: 0.8, weight: 2, color, fillColor: color }
    },
    [selectedArea, highlightedArea, getAreaTypeColor, draggedGroup, isDragOver]
  )

  // Update polygon styling with aggressive debouncing to prevent flickering
  const updatePolygonStyles = useCallback(() => {
    // Create a state signature to avoid unnecessary updates
    const stateSignature = `${selectedArea}_${highlightedArea}_${hoveredArea}`
    if (lastStyleUpdate.current === stateSignature) {
      return // Skip if no actual state change
    }
    lastStyleUpdate.current = stateSignature

    // Use stored polygon references for more reliable control
    polygonRefs.current.forEach((polygon, areaId) => {
      const isSelected = areaId === selectedArea
      const isHighlighted = areaId === highlightedArea
      const isHovered = areaId === hoveredArea
      const areaType = areaTypeMap.get(areaId) || 'land'

      const newStyle = getPolygonStyle(areaId, isSelected, isHighlighted, isHovered, areaType)

      // Always update style
      polygon.setStyle(newStyle)

      // Note: Visibility control is now handled separately in the immediate visibility useEffect
    })
  }, [selectedArea, highlightedArea, hoveredArea, getPolygonStyle, areaTypeMap])

  // Debounced style update function
  const debouncedStyleUpdate = useCallback(() => {
    if (styleUpdateTimeout.current) {
      clearTimeout(styleUpdateTimeout.current)
    }
    styleUpdateTimeout.current = setTimeout(() => {
      updatePolygonStyles()
    }, 100) // Aggressive debouncing for style updates
  }, [updatePolygonStyles])

  // Clear cache when selection changes
  useEffect(() => {
    polygonStyleCache.current.clear()
  }, [selectedArea])

  // Update polygon styles when state changes, with debouncing
  useEffect(() => {
    debouncedStyleUpdate()
  }, [debouncedStyleUpdate])

  // Immediate visibility update when selection changes
  useEffect(() => {
    console.log('🎯 Selection changed:', selectedArea ? `Selected ${selectedArea}` : 'No selection')
    console.log('🗺️ Available polygons:', Array.from(polygonRefs.current.keys()))

    // Clear the map and re-add only visible polygons
    spawnLayersRef.current.clearLayers()

    let visibleCount = 0
    let hiddenCount = 0

    polygonRefs.current.forEach((polygon, areaId) => {
      // Only add the selected polygon, or all polygons if none selected
      if (!selectedArea || areaId === selectedArea) {
        spawnLayersRef.current.addLayer(polygon)
        console.log('👁️ Showing polygon:', areaId)
        visibleCount++
      } else {
        console.log('🔍 Hiding polygon:', areaId)
        hiddenCount++
      }
    })

    console.log(`📊 Visibility result: ${visibleCount} visible, ${hiddenCount} hidden`)
  }, [selectedArea])

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = L.map(mapContainerRef.current, {
      center: [25.5854, -80.0081], // Miami area coordinates
      zoom: 15,
      zoomControl: true,
      attributionControl: false,
    })

    // Add satellite tiles instead of OpenStreetMap
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution:
          'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        maxZoom: 18,
      }
    ).addTo(map)

    // Add spawn areas layer
    spawnLayersRef.current.addTo(map)

    mapRef.current = map
    setMapInitialized(true)

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current)
      }
      if (highlightTimeout.current) {
        clearTimeout(highlightTimeout.current)
      }
      if (styleUpdateTimeout.current) {
        clearTimeout(styleUpdateTimeout.current)
      }
    }
  }, [])

  // Render polygons from spawnAreas prop
  useEffect(() => {
    if (!mapRef.current || !spawnLayersRef.current) return

    console.log('🗺️ Rendering polygons from spawnAreas prop:', spawnAreas.length)

    // Clear existing layers
    spawnLayersRef.current.clearLayers()
    polygonRefs.current.clear()

    spawnAreas.forEach((spawnArea) => {
      // Only render if coordinates are available (from KML or other sources)
      if (spawnArea.coordinates && spawnArea.coordinates.length > 0) {
        console.log(
          `📍 Creating polygon for ${spawnArea.name} with ${spawnArea.coordinates.length} coordinates`
        )

        // Create polygon from coordinates
        const isSelected = selectedArea === spawnArea.id
        const isHighlighted = highlightedArea === spawnArea.id
        const isHovered = hoveredArea === spawnArea.id
        const polygonStyle = getPolygonStyle(
          spawnArea.id,
          isSelected,
          isHighlighted,
          isHovered,
          spawnArea.type
        )
        const polygon = L.polygon(spawnArea.coordinates, polygonStyle)

        // Add event handlers
        polygon.on('click', () => {
          if (onSpawnAreaClick) {
            onSpawnAreaClick(spawnArea)
          }
        })

        polygon.on('mouseover', () => {
          setHighlightedAreaDebounced(spawnArea.id)
        })

        polygon.on('mouseout', () => {
          setHighlightedAreaDebounced(null)
        })

        // Store polygon reference
        polygonRefs.current.set(spawnArea.id, polygon)

        // Add to layer group
        spawnLayersRef.current.addLayer(polygon)
      }
    })

    console.log(`✅ Created ${polygonRefs.current.size} polygons on map`)

    // Auto-fit bounds to show all spawn areas when they're first loaded (only once)
    if (polygonRefs.current.size > 0 && mapRef.current && !boundsSet) {
      const group = new L.FeatureGroup()
      polygonRefs.current.forEach((polygon) => group.addLayer(polygon))
      if (group.getLayers().length > 0) {
        console.log('🔍 Auto-fitting map bounds to show all spawn areas (first time only)')
        mapRef.current.fitBounds(group.getBounds(), { padding: [20, 20] })
        setBoundsSet(true) // Prevent future auto-fitting
      }
    }
  }, [spawnAreas, getPolygonStyle, onSpawnAreaClick, setHighlightedAreaDebounced, boundsSet])

  // Use spawn areas directly from props
  const visibleSpawnAreas = selectedArea
    ? spawnAreas.filter((area) => area.id === selectedArea)
    : spawnAreas

  return (
    <ViewerContainer>
      <MapContainer
        ref={mapContainerRef}
        $isDragOver={isDragOver}
        $hasSelectedArea={!!selectedArea}
        onDragOver={(e) => {
          if (selectedArea && draggedGroup) {
            e.preventDefault()
            e.stopPropagation()
            console.log('🎯 Drag over map area')
            onDragOver && onDragOver()
          }
        }}
        onDragLeave={(e) => {
          if (selectedArea && draggedGroup) {
            e.stopPropagation()
            console.log('🚶 Drag leave map area')
            onDragLeave && onDragLeave()
          }
        }}
        onDrop={(e) => {
          if (selectedArea && draggedGroup) {
            e.preventDefault()
            e.stopPropagation()
            console.log('📦 Drop event on map area')
            onDrop && onDrop(e)
          }
        }}
      />

      <SpawnAreaSidebar>
        <SidebarHeader>
          <SidebarTitle>
            <Package size={16} />
            Spawn Areas
          </SidebarTitle>
          {selectedArea && <SidebarSubtitle>Showing only selected area</SidebarSubtitle>}
        </SidebarHeader>
        <SpawnAreasList>
          {visibleSpawnAreas.map((area) => {
            const areaGroupPlacements = groupPlacements.filter((p) => p.spawnAreaId === area.id)
            const groupCount = areaGroupPlacements.length
            const totalObjects = areaGroupPlacements.reduce((sum, placement) => {
              const settings = getSpawnSettings(placement.groupId, placement.spawnAreaId)
              return sum + (settings.objectCount || 1)
            }, 0)
            const isHovered = hoveredArea === area.id
            const isSelected = selectedArea === area.id
            const isHighlighted = highlightedArea === area.id

            return (
              <SpawnAreaItem
                key={area.id}
                $type={area.type}
                $isHovered={isHovered}
                $isSelected={isSelected}
                onMouseEnter={() => !selectedArea && setHighlightedAreaDebounced(area.id)}
                onMouseLeave={() => setHighlightedAreaDebounced(null)}
                onClick={() => {
                  console.log('🖱️ Sidebar area clicked:', area.name, 'ID:', area.id)
                  if (onSpawnAreaClick) {
                    console.log('📞 Calling onSpawnAreaClick for:', area.name)
                    onSpawnAreaClick(area)
                  } else {
                    console.log('❌ onSpawnAreaClick is undefined!')
                  }
                }}
              >
                <SpawnAreaName>{area.name}</SpawnAreaName>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <SpawnAreaType $type={area.type}>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor:
                          area.type === 'water'
                            ? '#60a5fa'
                            : area.type === 'land'
                            ? '#4ade80'
                            : '#fbbf24',
                      }}
                    />
                    {area.type}
                  </SpawnAreaType>

                  {groupCount > 0 && (
                    <Badge
                      style={{
                        background: getAreaTypeColor(area.type),
                        fontSize: '0.65rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                      }}
                    >
                      {groupCount}
                    </Badge>
                  )}
                </div>

                {/* Enhanced group summary */}
                {groupCount > 0 && (
                  <GroupSummary>
                    <div style={{ fontSize: '0.7rem', color: '#60a5fa', fontWeight: '600' }}>
                      {groupCount} group{groupCount !== 1 ? 's' : ''} • {totalObjects} object
                      {totalObjects !== 1 ? 's' : ''}
                    </div>
                  </GroupSummary>
                )}
              </SpawnAreaItem>
            )
          })}
        </SpawnAreasList>

        {/* Section 2: Total Objects Control */}
        {selectedArea &&
          (() => {
            const selectedAreaData = spawnAreas.find((area) => area.id === selectedArea)
            const areaGroupPlacements = groupPlacements.filter(
              (p) => p.spawnAreaId === selectedArea
            )

            if (!selectedAreaData) return null

            // Get the first placement to get the area-wide object count (they should all be the same)
            const areaObjectCount =
              areaGroupPlacements.length > 0
                ? getSpawnSettings(areaGroupPlacements[0].groupId, selectedArea).objectCount
                : 10

            return (
              <div
                style={{
                  margin: '0 1.25rem 0.75rem',
                  padding: '1rem',
                  background: '#2a2a2a',
                  border: '1px solid #333',
                  borderRadius: '8px',
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6
                      className="text-white mb-0"
                      style={{ fontSize: '0.85rem', fontWeight: '600' }}
                    >
                      Total Objects to Spawn
                    </h6>
                  </div>
                  <Form.Control
                    type="number"
                    size="sm"
                    min="1"
                    max="1000"
                    value={areaObjectCount}
                    onChange={(e) => {
                      const newCount = parseInt(e.target.value) || 1
                      // Update all groups in this area to the same count
                      areaGroupPlacements.forEach((placement) => {
                        updateSpawnSettings(placement.groupId, placement.spawnAreaId, {
                          objectCount: newCount,
                        })
                      })
                    }}
                    style={{
                      width: '80px',
                      background: '#1a1a1a',
                      border: '1px solid #555',
                      color: 'white',
                      textAlign: 'center',
                    }}
                  />
                </div>
              </div>
            )
          })()}

        {/* Section 3: Attached Groups */}
        {selectedArea &&
          (() => {
            const selectedAreaData = spawnAreas.find((area) => area.id === selectedArea)
            const areaGroupPlacements = groupPlacements.filter(
              (p) => p.spawnAreaId === selectedArea
            )

            if (!selectedAreaData) return null

            return (
              <div
                style={{
                  margin: '0 1.25rem 1.25rem',
                  padding: '1rem',
                  background: '#1e1e1e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                }}
              >
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#aaa',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '600',
                  }}
                >
                  Attached Groups ({areaGroupPlacements.length})
                </div>

                {areaGroupPlacements.length > 0 ? (
                  areaGroupPlacements.map((placement) => {
                    const group = availableGroups.find((g) => g.id === placement.groupId)

                    return (
                      <div
                        key={`${placement.groupId}-${placement.spawnAreaId}`}
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid #333',
                          borderRadius: '6px',
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: '0.8rem',
                              color: 'white',
                              fontWeight: '600',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {group?.name || 'Unknown Group'}
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#aaa' }}>
                            {group?.objects.length || 0} object types • {group?.type || 'unknown'}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          style={{
                            padding: '0.25rem 0.4rem',
                            fontSize: '0.65rem',
                          }}
                          onClick={() =>
                            removeGroupPlacement(placement.groupId, placement.spawnAreaId)
                          }
                        >
                          <Trash2 size={10} />
                        </Button>
                      </div>
                    )
                  })
                ) : (
                  <div className="text-center py-3" style={{ color: '#666' }}>
                    <Package size={16} style={{ marginBottom: '0.5rem' }} />
                    <div style={{ fontSize: '0.75rem' }}>No groups attached</div>
                    <div style={{ fontSize: '0.65rem', color: '#555' }}>
                      Drag groups onto the map
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
      </SpawnAreaSidebar>
    </ViewerContainer>
  )
}

export default MapViewer
